/*
Origin Core 1.0

called from /sections/product-recommendations.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <product-recommendations> that lazily loads product recommendations when the component enters the viewport. 
It uses an IntersectionObserver to trigger loading, fetches recommendations via AJAX, and caches results for efficiency. 
The component updates its content with the fetched recommendations and initializes a Splide carousel for displaying recommended products. 
It also observes attribute changes to reload recommendations when needed and handles errors gracefully. 
The component is registered if not already defined.

*/

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#loadRecommendations();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // Skip if product ID contains temporary markers (used to trigger reloads)
    if (productId.includes('_temp')) {
      return;
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // Ignore aborted requests (status 0) - they're expected when aborting previous requests
          if (result.status === 0) {
            return;
          }
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          
     
          
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;

          const productRecsSlideshow = new Splide(`[data-product-carousel-${this.dataset.sectionId}]`, {
            autoWidth: true,
            perMove: 1,
            easingFunc: true,
            pagination: false,
            type: 'slide',
            drag: true,
            arrows: true,
            gap: 20
          });

          const progress = productRecsSlideshow.root.querySelector('.my-slider-progress');
          const thumb = productRecsSlideshow.root.querySelector('.my-slider-progress-bar');
          let isSeeking = false;

          function getMetrics() {
            const track = productRecsSlideshow.Components.Elements.track;
            const list = productRecsSlideshow.Components.Elements.list;
            return { trackWidth: track.offsetWidth, listWidth: list.scrollWidth };
          }

          function updateScrollbar() {
            if (isSeeking) return;
            const { trackWidth, listWidth } = getMetrics();
            const arrows = productRecsSlideshow.root.querySelector('.splide__arrows');
            const nextBtn = productRecsSlideshow.root.querySelector('.splide__arrow--next');
            const prevBtn = productRecsSlideshow.root.querySelector('.splide__arrow--prev');

            if (listWidth <= trackWidth) {
              progress.classList.add('hidden');
              arrows?.classList.add('hidden');
              return;
            }

            const thumbWidthPct = (trackWidth / listWidth) * 100;
            const currentPos = Math.abs(productRecsSlideshow.Components.Move.getPosition());
            const maxPos = listWidth - trackWidth;
            const posRatio = maxPos > 0 ? Math.min(currentPos / maxPos, 1) : 0;
            const thumbLeftPct = posRatio * (100 - thumbWidthPct);

            thumb.style.width = thumbWidthPct + '%';
            thumb.style.left = thumbLeftPct + '%';

            const endIndex = productRecsSlideshow.Components.Controller.getEnd();
            const splideMaxPos = Math.abs(productRecsSlideshow.Components.Move.toPosition(endIndex, true));

            requestAnimationFrame(() => {
              if (nextBtn) nextBtn.disabled = currentPos >= splideMaxPos - 1;
              if (prevBtn) prevBtn.disabled = currentPos <= 1;
            });
          }

          productRecsSlideshow.on('mounted move', updateScrollbar);
          productRecsSlideshow.on('moved', function () {
            isSeeking = false;
            updateScrollbar();
          });

          // Click on track to jump to position
          progress.addEventListener('click', function (e) {
            if (e.target === thumb) return;
            const rect = progress.getBoundingClientRect();
            const clickRatio = (e.clientX - rect.left) / rect.width;
            const end = productRecsSlideshow.Components.Controller.getEnd();
            productRecsSlideshow.go(Math.round(clickRatio * end));
          });

          // Drag thumb
          thumb.addEventListener('mousedown', function (e) {
            e.preventDefault();
            const startX = e.clientX;
            const startLeft = parseFloat(thumb.style.left) || 0;
            thumb.classList.add('is-dragging');

            function onMouseMove(e) {
              const { trackWidth, listWidth } = getMetrics();
              const thumbWidthPct = (trackWidth / listWidth) * 100;
              const maxLeft = 100 - thumbWidthPct;
              const deltaPct = ((e.clientX - startX) / progress.offsetWidth) * 100;
              const newLeft = Math.max(0, Math.min(startLeft + deltaPct, maxLeft));
              thumb.style.left = newLeft + '%';
            }

            function onMouseUp() {
              thumb.classList.remove('is-dragging');
              document.removeEventListener('mousemove', onMouseMove);
              document.removeEventListener('mouseup', onMouseUp);
              const { trackWidth, listWidth } = getMetrics();
              const thumbWidthPct = (trackWidth / listWidth) * 100;
              const maxLeft = 100 - thumbWidthPct;
              const posRatio = maxLeft > 0 ? (parseFloat(thumb.style.left) || 0) / maxLeft : 0;
              const end = productRecsSlideshow.Components.Controller.getEnd();
              isSeeking = true;
              productRecsSlideshow.go(Math.round(posRatio * end));
            }

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
          });

          // Touch drag
          thumb.addEventListener('touchstart', function (e) {
            e.preventDefault();
            const startX = e.touches[0].clientX;
            const startLeft = parseFloat(thumb.style.left) || 0;
            thumb.classList.add('is-dragging');

            function onTouchMove(e) {
              const { trackWidth, listWidth } = getMetrics();
              const thumbWidthPct = (trackWidth / listWidth) * 100;
              const maxLeft = 100 - thumbWidthPct;
              const deltaPct = ((e.touches[0].clientX - startX) / progress.offsetWidth) * 100;
              const newLeft = Math.max(0, Math.min(startLeft + deltaPct, maxLeft));
              thumb.style.left = newLeft + '%';
            }

            function onTouchEnd() {
              thumb.classList.remove('is-dragging');
              thumb.removeEventListener('touchmove', onTouchMove);
              thumb.removeEventListener('touchend', onTouchEnd);
              const { trackWidth, listWidth } = getMetrics();
              const thumbWidthPct = (trackWidth / listWidth) * 100;
              const maxLeft = 100 - thumbWidthPct;
              const posRatio = maxLeft > 0 ? (parseFloat(thumb.style.left) || 0) / maxLeft : 0;
              const end = productRecsSlideshow.Components.Controller.getEnd();
              isSeeking = true;
              productRecsSlideshow.go(Math.round(posRatio * end));
            }

            thumb.addEventListener('touchmove', onTouchMove, { passive: false });
            thumb.addEventListener('touchend', onTouchEnd);
          }, { passive: false });

          productRecsSlideshow.mount();


        } else {

          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        // Ignore AbortError - it's expected when aborting previous requests
        if (e.name !== 'AbortError') {
          this.#handleError(e);
        }
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } catch (error) {
      // Ignore AbortError - it's expected when aborting previous requests
      if (error.name === 'AbortError') {
        return { success: false, status: 0 };
      }
      throw error;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    //this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
